import api from "./api";
import { News, PaginatedResponse } from "@/types";

/**
 * Query parameters supported by the GET /news endpoint.
 */
interface NewsQueryParams {
  page?: number;
  limit?: number;
  category?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Service layer for News API endpoints.
 * Base path: /api/v1/news
 */
export const newsService = {
  /**
   * Fetch paginated news articles.
   * API returns: { docs: [...], total, page, limit }
   * @param params - Optional pagination & sorting params
   * @returns PaginatedResponse of News items
   */
  getNews: async (
    params?: NewsQueryParams,
  ): Promise<PaginatedResponse<News>> => {
    const data = await api.get<unknown, PaginatedResponse<News>>("/news", {
      params,
    });

    const newsItems = Array.isArray(data.docs)
      ? data.docs
      : Array.isArray(data)
        ? (data as unknown as News[])
        : [];

    const mappedDocs = newsItems.map((item) => ({
      ...item,
      id: (item as unknown as Record<string, string>)._id || item.id,
    }));

    return {
      ...data,
      docs: mappedDocs,
    };
  },

  /**
   * Fetch a specific news article by its ID.
   * After interceptor unwrap, data may be the article directly or wrapped in { doc: ... }.
   * @param id - The news article ID
   */
  getNewsById: async (id: string): Promise<News> => {
    const raw: Record<string, unknown> = await api.get(`/news/${id}`);
    const article = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(article as unknown as News),
      id: (article._id as string) || (article.id as string),
    };
  },

  /**
   * Fetch a specific news article by its slug.
   * @param slug - The news article slug
   */
  getNewsBySlug: async (slug: string): Promise<News> => {
    const raw: Record<string, unknown> = await api.get(`/news/${slug}`);
    const article = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(article as unknown as News),
      id: (article._id as string) || (article.id as string),
    };
  },

  /**
   * Create a new news article.
   * @param formData - FormData containing news fields and image file
   */
  createNews: async (formData: FormData): Promise<News> => {
    const raw: any = await api.post("/news", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600000,
    });
    const article = raw?.doc || raw;
    return {
      ...(article as News),
      id: article._id || article.id,
    };
  },

  /**
   * Update an existing news article.
   * @param id - The news article ID
   * @param formData - FormData containing updated news fields and/or image file
   */
  updateNews: async (id: string, formData: FormData): Promise<News> => {
    const raw: any = await api.patch(`/news/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600000,
    });
    const article = raw?.doc || raw;
    return {
      ...(article as News),
      id: article._id || article.id,
    };
  },

  /**
   * Delete a news article.
   * @param id - The news article ID
   */
  deleteNews: async (id: string): Promise<void> => {
    await api.delete(`/news/${id}`);
  },
};
